import type { CredentialFormRendererProps, CredentialFormControl } from '../../types';

/**
 * Built-in default `CredentialFormRenderer` — today's plain-HTML field rendering, extracted
 * verbatim out of `CredentialEditor.tsx` (no behavior change) so it can serve as the fallback
 * `useCredentialFormRenderer()` resolves to when a consuming app supplies no custom renderer via
 * `<AiAgentAdminApiProvider formRenderer={...}>`. octopus-admin/did-admin/octopus-a2a all still
 * resolve to exactly this — zero behavior change for them.
 *
 * Renders `CredentialFormSchema.controls` (text/password/textarea/select/checkbox/number) with
 * plain HTML controls — deliberately does NOT depend on Atlas Forms (see
 * credentialAdminApiClient.ts's header comment). A consumer that wants the original Atlas-Forms-
 * driven dynamic per-type form (e.g. Flow Studio) supplies its own renderer instead — see
 * flow-studio-designer's `AtlasCredentialFormAdapter.tsx`.
 */
export function defaultCredentialFormRenderer({ schema, values, onFieldChange }: CredentialFormRendererProps) {
  const controls = schema.controls ?? [];
  return (
    <>
      {controls.map((control) => (
        <DynamicField
          key={control.id}
          control={control}
          value={values[control.id]}
          onChange={(v) => onFieldChange(control.id, v)}
        />
      ))}
      {controls.length === 0 && (
        <div className="aaa-form-group-full aaa-entity-dropdown-empty">
          No form fields defined for this credential type yet (no Atlas Form / schema found for it).
        </div>
      )}
    </>
  );
}

function DynamicField({ control, value, onChange }: { control: CredentialFormControl; value: unknown; onChange: (value: unknown) => void }) {
  const full = control.type === 'textarea';
  const wrapClass = full ? 'aaa-form-group aaa-form-group-full' : 'aaa-form-group';

  if (control.type === 'checkbox') {
    return (
      <div className={wrapClass}>
        <label className="aaa-checkbox">
          <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} />
          {control.label}
        </label>
      </div>
    );
  }

  if (control.type === 'select') {
    return (
      <div className={wrapClass}>
        <label>
          {control.label}
          {control.required ? ' *' : ''}
        </label>
        <select value={(value as string) ?? ''} onChange={(e) => onChange(e.target.value)}>
          <option value="">— Select —</option>
          {(control.options ?? []).map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {control.helpText && <small>{control.helpText}</small>}
      </div>
    );
  }

  if (control.type === 'textarea') {
    return (
      <div className={wrapClass}>
        <label>
          {control.label}
          {control.required ? ' *' : ''}
        </label>
        <textarea rows={3} placeholder={control.placeholder} value={(value as string) ?? ''} onChange={(e) => onChange(e.target.value)} />
        {control.helpText && <small>{control.helpText}</small>}
      </div>
    );
  }

  const inputType = control.type === 'password' ? 'password' : control.type === 'number' ? 'number' : 'text';
  return (
    <div className={wrapClass}>
      <label>
        {control.label}
        {control.required ? ' *' : ''}
      </label>
      <input
        type={inputType}
        placeholder={control.placeholder}
        value={(value as string | number) ?? ''}
        onChange={(e) => onChange(inputType === 'number' ? (e.target.value ? Number(e.target.value) : null) : e.target.value)}
      />
      {control.helpText && <small>{control.helpText}</small>}
    </div>
  );
}
